import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Globe, Palette } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { useLanguage } from '../context/LanguageContext';
import { PLATFORM_LANG_LIST, localizePlatformLabel } from '../lib/platforms';

// Brand preferences — content_language + platform_languages (from old "Schreiben")
// and image_style + style_strength (from old "Visuell"). All four live in ONE
// preferences doc, so this component is the SINGLE writer. Every save is
// fetch-first: read current prefs, merge the one change, POST the full object —
// so no field ever overwrites another in the same doc. Endpoint unchanged:
// GET/POST /brand/preferences. Does NOT touch the image pipeline.

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const CONTENT_LANGS = [
  { key: 'de', label: 'Deutsch' }, { key: 'en', label: 'English' },
  { key: 'pl', label: 'Polski' }, { key: 'fr', label: 'Français' },
  { key: 'es', label: 'Español' }, { key: 'it', label: 'Italiano' },
  { key: 'nl', label: 'Nederlands' }, { key: 'pt', label: 'Português' },
  { key: 'cs', label: 'Čeština' }, { key: 'hu', label: 'Magyar' },
  { key: 'ro', label: 'Română' }, { key: 'sv', label: 'Svenska' },
  { key: 'tr', label: 'Türkçe' },
];

const PLATFORM_LANG_OPTIONS = ['de', 'en', 'pl', 'fr', 'es', 'it', 'ro'];

const BrandDnaPreferences = ({ getAuthHeader }) => {
  const { t, lang } = useLanguage();
  const [prefs, setPrefs] = useState({
    content_language: 'de', image_style: 'illustrations',
    platform_languages: {}, style_strength: 'exakt',
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/brand/preferences`, { headers: getAuthHeader() });
      setPrefs(p => ({
        content_language: r.data?.content_language || p.content_language,
        image_style: r.data?.image_style || p.image_style,
        platform_languages: r.data?.platform_languages || {},
        style_strength: (['locker', 'treffen', 'exakt'].includes(r.data?.style_strength) ? r.data.style_strength : p.style_strength),
      }));
    } catch { }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  // fetch-first save: never clobber another field in the same doc.
  const savePref = async (patch) => {
    setSaving(true);
    setPrefs(p => ({ ...p, ...patch })); // optimistic
    try {
      const cur = await axios.get(`${API}/brand/preferences`, { headers: getAuthHeader() });
      const merged = {
        content_language: cur.data?.content_language || 'de',
        image_style: cur.data?.image_style || 'illustrations',
        platform_languages: cur.data?.platform_languages || {},
        style_strength: cur.data?.style_strength || 'exakt',
        ...patch,
      };
      await axios.post(`${API}/brand/preferences`, merged, { headers: getAuthHeader() });
    } catch { toast.error('Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  const setPlatformLang = (platformKey, l) => {
    const updated = { ...prefs.platform_languages };
    if (updated[platformKey] === l) delete updated[platformKey];
    else updated[platformKey] = l;
    savePref({ platform_languages: updated });
  };

  const IMAGE_STYLES = [
    { key: 'illustrations', label: t.settings.visual.styleIllustrations },
    { key: 'ai_people', label: t.settings.visual.styleAiPeople },
    { key: 'photography', label: t.settings.visual.stylePhotography },
    { key: 'mixed', label: t.settings.visual.styleMixed },
  ];
  const FIDELITY = [
    { key: 'locker', label: t.settings.styleStrength.lockerLabel },
    { key: 'treffen', label: t.settings.styleStrength.treffenLabel },
    { key: 'exakt', label: t.settings.styleStrength.exaktLabel },
  ];

  return (
    <div className="space-y-4">
      {/* Content language */}
      <Card className="bg-[#1A2B42] border-transparent">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2"><Globe className="w-4 h-4 text-[#00D9C8]" />{t.settings.writing.contentLanguage}</CardTitle>
          <CardDescription className="text-[#A0AEC0] text-sm">{t.settings.writing.contentLanguageDesc}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {CONTENT_LANGS.map(l => (
              <button key={l.key} disabled={saving} onClick={() => savePref({ content_language: l.key })}
                className={`py-2 px-3 rounded-lg text-sm font-medium transition-all border ${prefs.content_language === l.key ? 'bg-[#00D9C8]/20 text-[#00D9C8] border-[#00D9C8]/40' : 'bg-[#0A1628] text-[#5A6C7D] border-transparent hover:text-[#A0AEC0]'}`}
                data-testid={`settings-lang-${l.key}`}>{l.label}</button>
            ))}
          </div>
          {/* Per-platform overrides */}
          <div className="border-t border-[#0A1628] pt-3">
            <p className="text-xs text-[#5A6C7D] mb-3">{t.settings.writing.platformMapping}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {PLATFORM_LANG_LIST.map(pl => {
                const Icon = pl.icon;
                const currentLang = prefs.platform_languages?.[pl.key] || '';
                return (
                  <div key={pl.key} className="flex items-center gap-2 bg-[#0A1628] rounded-lg px-3 py-2" data-testid={`platform-lang-${pl.key}`}>
                    <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: pl.color }} />
                    <span className="text-xs text-[#A0AEC0] flex-1 truncate">{localizePlatformLabel(pl, lang)}</span>
                    <div className="flex gap-1">
                      {PLATFORM_LANG_OPTIONS.map(l => (
                        <button key={l} disabled={saving} onClick={() => setPlatformLang(pl.key, l)}
                          className={`px-1.5 py-0.5 rounded text-[10px] font-mono transition-all ${currentLang === l ? 'bg-[#00D9C8]/20 text-[#00D9C8] border border-[#00D9C8]/40' : 'text-[#5A6C7D] hover:text-[#A0AEC0]'}`}>
                          {l.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Image style */}
      <Card className="bg-[#1A2B42] border-transparent">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2"><Palette className="w-4 h-4 text-[#00D9C8]" />{t.settings.visual.imageStyle}</CardTitle>
          <CardDescription className="text-[#A0AEC0] text-sm">{t.settings.visual.imageStyleDesc}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {IMAGE_STYLES.map(s => (
              <button key={s.key} disabled={saving} onClick={() => savePref({ image_style: s.key })}
                className={`py-2.5 px-4 rounded-lg text-left transition-all border ${prefs.image_style === s.key ? 'bg-[#00D9C8]/20 text-[#00D9C8] border-[#00D9C8]/40' : 'bg-[#0A1628] text-[#5A6C7D] border-transparent hover:text-[#A0AEC0]'}`}
                data-testid={`settings-image-style-${s.key}`}>
                <p className="text-sm font-medium">{s.label}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Style fidelity */}
      <Card className="bg-[#1A2B42] border-transparent">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2"><Palette className="w-4 h-4 text-[#00D9C8]" />{t.settings.styleStrength.title}</CardTitle>
          <CardDescription className="text-[#A0AEC0] text-sm">{t.settings.styleStrength.description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {FIDELITY.map(s => (
              <button key={s.key} disabled={saving} onClick={() => savePref({ style_strength: s.key })}
                className={`py-3 px-4 rounded-lg text-left transition-all border ${prefs.style_strength === s.key ? 'bg-[#00D9C8]/15 text-[#00D9C8] border-[#00D9C8]/40' : 'bg-[#0A1628] text-[#5A6C7D] border-transparent hover:text-[#A0AEC0]'}`}>
                <p className="text-sm font-medium">{s.label}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BrandDnaPreferences;
